import * as React from 'react';
import { Menu, useResourceDefinitions } from 'react-admin';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PrintIcon from '@mui/icons-material/Print';
import AttractionsIcon from '@mui/icons-material/Attractions';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useTranslation } from 'react-i18next';
import { isAdmin } from '../../core/roles.ts';
import { useShabbatVenues } from '../../pages/shabbat/shabbatShared.tsx';
import { isShabbatOnlyLogin } from '../../core/shabbatOnly.ts';
import { PARTNERS_RESOURCE } from '../../pages/marketing/PartnersPages.tsx';

// Listed under Marketing by hand, not with the other resources.
const GROUPED_RESOURCES = [PARTNERS_RESOURCE];
//TODO: move styles to a separate file if possible
export const MyMenu = () => {
  const resources = useResourceDefinitions();
  const { t } = useTranslation();

  // Settings is a CustomRoute, not a Resource, so it is not in `resources` and
  // has to be listed by hand. Admins only, matching the route: the support
  // number is not a vendor's to change.
  const admin = isAdmin();

  // Shabbat meals: always for an admin (Ariel, 25 Oct 2026) - the pages say
  // so when no venue hosts them yet, and keep a switched-off venue's history.
  // A restaurant login only once an admin has switched "Hosts Shabbat meals"
  // on for its venue; the server decides, nothing shows until it answers.
  const shabbatVenues = useShabbatVenues();
  const hostsShabbat = admin || (!!shabbatVenues && shabbatVenues.length > 0);
  // A Chabad House: its Shabbat pages first, then its venue; no orders.
  const shabbatOnly = isShabbatOnlyLogin();

  return (
    <Menu
      sx={{
        '& .RaMenuItemLink-active': {
          bgcolor: 'var(--color-secondary)',
          color: 'var(--color-text-secondary) !important',
        },
        '& .RaMenuItemLink-root': {
          color: 'var(--color-text-disabled)',
        },
        '& .RaMenuItemLink-active:hover': {
          bgcolor: 'action.selected',
        },
      }}
    >
      {!shabbatOnly && Object.keys(resources).filter((name) => !GROUPED_RESOURCES.includes(name)).map((name) => (
        <Menu.ResourceItem key={name} name={name} />
      ))}
      {(hostsShabbat || shabbatOnly) && (
        <>
          <Menu.Item
            to="/shabbat/meals"
            primaryText={t('resources.shabbatMeals', 'Shabbat meals')}
            leftIcon={<RestaurantMenuIcon />}
          />
          <Menu.Item
            to="/shabbat/registrations"
            primaryText={t('resources.shabbatRegistrations', 'Shabbat registrations')}
            leftIcon={<ListAltIcon />}
          />
          <Menu.Item
            to="/shabbat/guest-list"
            primaryText={t('resources.shabbatGuestList', 'Shabbat guest list')}
            leftIcon={<PrintIcon />}
          />
        </>
      )}
      {shabbatOnly && Object.keys(resources).filter((name) => !GROUPED_RESOURCES.includes(name)).map((name) => (
        <Menu.ResourceItem key={name} name={name} />
      ))}
      {/* Order history: today's and past orders in one page (23 Oct 2026). Not for a Chabad House. */}
      {!shabbatOnly && <Menu.Item
        to="/orders/history"
        primaryText={t('resources.orderHistory', 'Order history')}
        leftIcon={<HistoryIcon />}
      />}
      {admin && (
        <>
          {/* Attractions: a parent that stays highlighted on any of its pages
              (react-admin matches it as a prefix), and its pages under it. */}
          <Menu.Item
            to="/attractions"
            primaryText={t('resources.attractions', 'Attractions')}
            leftIcon={<AttractionsIcon />}
          />
          {[
            { to: '/attractions/catalogue', label: t('resources.attractionsCatalogue', 'Catalogue') },
            { to: '/attractions/collections', label: t('resources.attractionsCollections', 'Collections') },
            { to: '/attractions/rules', label: t('resources.attractionsRules', 'Rules') },
            { to: '/attractions/bookings', label: t('resources.attractionsBookings', 'Bookings') },
          ].map(item => (
            <Menu.Item
              key={item.to}
              to={item.to}
              primaryText={item.label}
              sx={{
                pl: 4,
                '&&.RaMenuItemLink-active': { bgcolor: 'transparent', fontWeight: 700 },
              }}
            />
          ))}
          <Menu.Item
            to="/credit"
            primaryText={t('resources.credit', 'Credit report')}
            leftIcon={<AccountBalanceWalletIcon />}
          />
          {/* Marketing pushes (cross-venue, finishing soon): stats and copy. Admin only.
              Its one sub-page sits under it the way the Attractions pages do; the
              parent stays highlighted on it (prefix match). */}
          <Menu.Item
            to="/notifications"
            primaryText={t('resources.notifications', 'Notifications')}
            leftIcon={<NotificationsActiveIcon />}
          />
          <Menu.Item
            to="/notifications/where"
            primaryText={t('resources.notificationsWhere', 'Where are my customers')}
            sx={{
              pl: 4,
              '&&.RaMenuItemLink-active': { bgcolor: 'transparent', fontWeight: 700 },
            }}
          />
          <Menu.Item
            to="/notifications/queue"
            primaryText={t('resources.notificationsQueue', 'Push queue')}
            sx={{
              pl: 4,
              '&&.RaMenuItemLink-active': { bgcolor: 'transparent', fontWeight: 700 },
            }}
          />
          {/* Marketing (1 Nov 2026): partners, their statements and where customers
              come from. "Partners" sits at /admin/partners (a react-admin
              resource), so the parent leads there and the sub-items carry
              their own highlight. */}
          <Menu.Item
            to="/marketing"
            primaryText={t('resources.marketing')}
            leftIcon={<CampaignIcon />}
          />
          {[
            { to: `/${PARTNERS_RESOURCE}`, label: t('resources.partners') },
            { to: '/marketing/partner-statement', label: t('resources.partnerStatement') },
            { to: '/marketing/where-from', label: t('resources.acquisitionReport') },
          ].map(item => (
            <Menu.Item
              key={item.to}
              to={item.to}
              primaryText={item.label}
              sx={{
                pl: 4,
                '&&.RaMenuItemLink-active': { bgcolor: 'transparent', fontWeight: 700 },
              }}
            />
          ))}
          <Menu.Item
            to="/delivery-priority"
            primaryText={t('resources.deliveryPriority', 'Delivery priority')}
            leftIcon={<LocalShippingIcon />}
          />
          <Menu.Item
            to="/settings"
            primaryText={t('resources.settings', 'Settings')}
            leftIcon={<SettingsIcon />}
          />
        </>
      )}
    </Menu>
  );
};
